import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { KibDateField } from '@chewy/kib-calendar-react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibField, KibSelect } from '@chewy/kib-fields-new-react'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react'
import classNames from 'classnames'
import { type SignatureOption, SignatureView } from '@pbt/pbt-ui-components'

import { KibUserSelect } from '~/components/common/inputs/user-select/KibUserSelect'
import { UserSelectFilterScope } from '~/components/common/inputs/UserSelect'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './ChirpChargesVaccineDetails.module.scss'

export interface VaccineDetailRow {
  doseType: string
  expires: string
  vaccineType: string
}

export interface VaccineDetailOption {
  label: string
  value: string
}

export interface InventoryDetails {
  dateGiven: string
  lotNumber: string
  manufacturedBy: string
  name: string
  serialNumber: string
}

export interface VaccineDetailsTopRow {
  assignedVeterinarian: string
  dateGiven: string
  deliveryMethod: string
}

export interface PatientDetails {
  animalControlLicense: string
  color: string
  license: string
  markingsTattoo: string
  microchip: string
  rabiesTagNumber: string
}

export interface ChirpChargesVaccineDetailsProps {
  comboRowCount?: number
  deliveryMethodOptions?: VaccineDetailOption[]
  disabled?: boolean
  doseTypeOptions?: VaccineDetailOption[]
  inventoryAlertDescription?: string
  inventoryAlertHeading?: string
  inventoryDetails?: InventoryDetails
  isComboVaccine: boolean
  onChooseFromInventory?: () => void
  onConfirmInventoryDetails?: () => void
  onEditSignature?: () => void
  onInventoryDetailsChange?: (details: InventoryDetails) => void
  onPatientDetailsChange?: (details: PatientDetails) => void
  onRowsChange: (rows: VaccineDetailRow[]) => void
  onSelectSignature?: (signer: SignatureOption) => void
  onVaccineDetailsTopRowChange?: (row: VaccineDetailsTopRow) => void
  patientDetails?: PatientDetails
  rows: VaccineDetailRow[]
  showInventoryActions?: boolean
  showInventoryAlert?: boolean
  showInventoryDetails?: boolean
  showPatientDetails?: boolean
  showSignature?: boolean
  showVaccineDetailsSection?: boolean
  signatureOptions?: SignatureOption[]
  signatureString?: string
  signerName?: string
  vaccineDetailsTopRow?: VaccineDetailsTopRow
  vaccineSectionHeading?: string
  vaccineTypeOptions?: VaccineDetailOption[]
  validationMessage?: string
}

const ChirpChargesVaccineDetails = ({
  isComboVaccine,
  rows,
  onRowsChange,
  comboRowCount = 2,
  deliveryMethodOptions = [],
  vaccineTypeOptions = [],
  doseTypeOptions = [],
  validationMessage,
  showSignature = false,
  signatureOptions = [],
  signatureString = '',
  signerName = '',
  disabled = false,
  onSelectSignature,
  onEditSignature,
  onChooseFromInventory,
  onConfirmInventoryDetails,
  onPatientDetailsChange,
  onVaccineDetailsTopRowChange,
  showInventoryDetails = false,
  showInventoryActions = false,
  inventoryDetails,
  onInventoryDetailsChange,
  showInventoryAlert = false,
  inventoryAlertHeading,
  inventoryAlertDescription,
  patientDetails,
  showPatientDetails = false,
  showVaccineDetailsSection = false,
  vaccineDetailsTopRow,
  vaccineSectionHeading,
}: ChirpChargesVaccineDetailsProps) => {
  const { t } = useTranslation(['Common', 'Dialogs', 'Soap'])
  const assignedVetUserSelectStateId = 'vaccine-assigned-vet'

  const rowCount = isComboVaccine ? comboRowCount : 1
  const rowKeys = useMemo(
    () =>
      Array.from({ length: rowCount }, (_, index) => `vaccine-row-${index}`),
    [rowCount],
  )
  const emptyRow: VaccineDetailRow = {
    doseType: doseTypeOptions[0]?.value ?? '',
    expires: '',
    vaccineType: vaccineTypeOptions[0]?.value ?? '',
  }
  const displayRows = Array.from(
    { length: rowCount },
    (_, i) => rows[i] ?? { ...emptyRow },
  )

  const updateRow = (index: number, updates: Partial<VaccineDetailRow>) => {
    const next = Array.from({ length: rowCount }, (_, i) =>
      i === index
        ? { ...(rows[i] ?? emptyRow), ...updates }
        : (rows[i] ?? emptyRow),
    )
    onRowsChange(next)
  }

  const inventory = inventoryDetails ?? {
    dateGiven: '',
    lotNumber: '',
    manufacturedBy: '',
    name: '',
    serialNumber: '',
  }

  const updateInventory = (updates: Partial<InventoryDetails>) => {
    const next: InventoryDetails = {
      dateGiven: updates.dateGiven ?? inventory.dateGiven ?? '',
      lotNumber: updates.lotNumber ?? inventory.lotNumber ?? '',
      manufacturedBy: updates.manufacturedBy ?? inventory.manufacturedBy ?? '',
      name: updates.name ?? inventory.name ?? '',
      serialNumber: updates.serialNumber ?? inventory.serialNumber ?? '',
    }
    onInventoryDetailsChange?.(next)
  }

  const vaccineTop = vaccineDetailsTopRow ?? {
    assignedVeterinarian: '',
    dateGiven: '',
    deliveryMethod: '',
  }
  const updateVaccineTop = (updates: Partial<VaccineDetailsTopRow>) => {
    onVaccineDetailsTopRowChange?.({ ...vaccineTop, ...updates })
  }

  const patient = patientDetails ?? {
    animalControlLicense: '',
    color: '',
    license: '',
    markingsTattoo: '',
    microchip: '',
    rabiesTagNumber: '',
  }
  const updatePatient = (updates: Partial<PatientDetails>) => {
    onPatientDetailsChange?.({ ...patient, ...updates })
  }

  const renderInventoryFields = (isAlertVariant: boolean) => (
    <>
      <div className={styles.inventoryFieldsGrid}>
        <div
          className={classNames(
            styles.inventoryField,
            styles.inventoryFieldName,
          )}
        >
          <KibField
            required
            disabled={disabled}
            label={t('Common:NAME')}
            value={inventory.name}
            onChange={(e) => updateInventory({ name: e.target.value })}
          />
        </div>
        <div className={styles.inventoryField}>
          <KibField
            required
            disabled={disabled}
            label={t('Common:MANUFACTURED_BY')}
            value={inventory.manufacturedBy}
            onChange={(e) =>
              updateInventory({ manufacturedBy: e.target.value })
            }
          />
        </div>
        <div className={styles.inventoryField}>
          <KibField
            required
            disabled={disabled}
            label={t('Common:LOT_NUMBER')}
            value={inventory.lotNumber}
            onChange={(e) => updateInventory({ lotNumber: e.target.value })}
          />
        </div>
        <div className={styles.inventoryField}>
          <KibField
            required
            disabled={disabled}
            label={t('Common:SERIAL_NUMBER')}
            value={inventory.serialNumber}
            onChange={(e) => updateInventory({ serialNumber: e.target.value })}
          />
        </div>
        <div
          className={classNames(
            styles.inventoryField,
            styles.inventoryDateField,
          )}
        >
          <KibDateField
            required
            showCalendar
            disabled={disabled}
            format="MM/dd/yyyy"
            help=""
            label={t('Dialogs:VACCINE_DETAILS_DIALOG.DATE')}
            placeholder="mm/dd/yyyy"
            value={inventory.dateGiven}
            onChange={(e) => updateInventory({ dateGiven: e.target.value })}
            {...(!isAlertVariant ? { invalid: false } : {})}
          />
        </div>
      </div>
      {showInventoryActions && (
        <div className={styles.inventoryActions}>
          <KibButtonNew
            {...(isAlertVariant
              ? { theme: 'utility' as const }
              : { emphasis: 'primary' as const })}
            onClick={onConfirmInventoryDetails}
          >
            {t('Dialogs:VACCINE_DETAILS_DIALOG.YES_DETAILS_CORRECT')}
          </KibButtonNew>
          <KibButtonNew
            emphasis="secondary"
            {...(isAlertVariant ? { theme: 'utility' as const } : {})}
            onClick={onChooseFromInventory}
          >
            {t('Common:CHOOSE_FROM_INVENTORY')}
          </KibButtonNew>
        </div>
      )}
    </>
  )

  return (
    <div className={styles.root}>
      {showInventoryDetails && (
        <section className={styles.inventorySection}>
          <h3 className={styles.sectionHeading}>
            {t('Dialogs:VACCINE_DETAILS_DIALOG.INVENTORY_DETAILS')}
          </h3>

          {showInventoryAlert ? (
            <div className={styles.alertContainer}>
              <KibTopLevelAlert
                heading={
                  inventoryAlertHeading ??
                  t(
                    'Dialogs:VACCINE_DETAILS_DIALOG.CONFIRM_INVENTORY_DETAILS_HEADING',
                  )
                }
                severity="warning"
              >
                <p className={styles.inventoryAlertDescription}>
                  {inventoryAlertDescription ??
                    t(
                      'Dialogs:VACCINE_DETAILS_DIALOG.CONFIRM_INVENTORY_DETAILS_DESCRIPTION',
                    )}
                </p>
                {renderInventoryFields(true)}
              </KibTopLevelAlert>
            </div>
          ) : (
            renderInventoryFields(false)
          )}
        </section>
      )}

      {showVaccineDetailsSection && (
        <section className={styles.vaccineDetailsSection}>
          <h3 className={styles.sectionHeading}>
            {t('Dialogs:VACCINE_DETAILS_DIALOG.VACCINE_DETAILS')}
          </h3>
          <div className={styles.vaccineTopRow}>
            <div className={classNames(styles.fieldVaccineType)}>
              <KibUserSelect
                required
                disabled={disabled}
                filterScope={UserSelectFilterScope.Doctors}
                label={t(
                  'Dialogs:VACCINE_DETAILS_DIALOG.ASSIGNED_VETERINARIAN',
                )}
                placeHolder={t('Common:SEARCH_ACTION')}
                userSelectStateId={assignedVetUserSelectStateId}
                value={vaccineTop.assignedVeterinarian}
                onChange={(value) =>
                  updateVaccineTop({
                    assignedVeterinarian: value,
                  })
                }
              />
            </div>
            <div className={styles.fieldDoseType}>
              <KibDateField
                required
                showCalendar
                disabled={disabled}
                format="MM/dd/yyyy"
                help=""
                label={t('Common:DATE_GIVEN_VACCINE')}
                placeholder="mm/dd/yyyy"
                value={vaccineTop.dateGiven}
                onChange={(e) =>
                  updateVaccineTop({ dateGiven: e.target.value })
                }
              />
            </div>
            <div className={styles.fieldExpires}>
              <KibSelect
                required
                disabled={disabled}
                label={t('Common:DRUG_DELIVERY_METHOD')}
                value={vaccineTop.deliveryMethod}
                onChange={(e) =>
                  updateVaccineTop({ deliveryMethod: e.target.value })
                }
              >
                {deliveryMethodOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </KibSelect>
            </div>
          </div>
        </section>
      )}

      {validationMessage != null && validationMessage !== '' && (
        <div className={styles.alertContainer}>
          <KibTopLevelAlert heading={validationMessage} severity="warning" />
        </div>
      )}

      {vaccineSectionHeading != null && vaccineSectionHeading !== '' && (
        <h3 className={styles.vaccineSectionHeading}>
          {vaccineSectionHeading}
        </h3>
      )}

      <div className={styles.rowsContainer}>
        {displayRows.map((row, index) => (
          <div className={styles.vaccineRow} key={rowKeys[index]}>
            <div className={classNames(styles.fieldVaccineType)}>
              <KibSelect
                required
                disabled={disabled}
                label={t('Common:VACCINE_TYPE')}
                value={row.vaccineType}
                onChange={(e) =>
                  updateRow(index, { vaccineType: e.target.value })
                }
              >
                {vaccineTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </KibSelect>
            </div>
            <div className={styles.fieldDoseType}>
              <KibSelect
                required
                disabled={disabled}
                label={t('Soap:ADDITIONAL_VACCINE_DETAILS.VACCINE_DOSE_TYPE')}
                value={row.doseType}
                onChange={(e) => updateRow(index, { doseType: e.target.value })}
              >
                {doseTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </KibSelect>
            </div>
            <div className={styles.fieldExpires}>
              <KibDateField
                required
                showCalendar
                disabled={disabled}
                format="MM/dd/yyyy"
                help=""
                label={t('Common:EXPIRES')}
                placeholder="mm/dd/yyyy"
                value={row.expires}
                onChange={(e) => updateRow(index, { expires: e.target.value })}
              />
            </div>
          </div>
        ))}
      </div>

      {showPatientDetails && (
        <section className={styles.patientDetailsSection}>
          <h3 className={styles.sectionHeading}>
            {t('Dialogs:VACCINE_DETAILS_DIALOG.PATIENT_DETAILS')}
          </h3>
          <div className={styles.patientDetailsGrid}>
            <div className={styles.patientDetailsRow2}>
              <KibField
                required
                disabled={disabled}
                label={t('Common:RABIES_TAG')}
                value={patient.rabiesTagNumber}
                onChange={(e) =>
                  updatePatient({ rabiesTagNumber: e.target.value })
                }
              />
              <KibField
                required
                disabled={disabled}
                label={t('Common:MICROCHIP')}
                placeholder={t('Dialogs:VACCINE_DETAILS_DIALOG.ENTER_NUMBER')}
                value={patient.microchip}
                onChange={(e) => updatePatient({ microchip: e.target.value })}
              />
            </div>
            <div className={styles.patientDetailsRow3}>
              <KibField
                required
                disabled={disabled}
                label={t('Common:COLOR')}
                placeholder={t('Dialogs:VACCINE_DETAILS_DIALOG.ENTER_COLOR')}
                value={patient.color}
                onChange={(e) => updatePatient({ color: e.target.value })}
              />
              <KibField
                required
                disabled={disabled}
                label={t('Common:MARKINGS_OR_TATTOO')}
                placeholder={t('Dialogs:VACCINE_DETAILS_DIALOG.LIST_MARKINGS')}
                value={patient.markingsTattoo}
                onChange={(e) =>
                  updatePatient({ markingsTattoo: e.target.value })
                }
              />
              <KibField
                required
                disabled={disabled}
                label={t(
                  'Dialogs:VACCINE_DETAILS_DIALOG.ANIMAL_CONTROL_LICENSE',
                )}
                placeholder={t('Dialogs:VACCINE_DETAILS_DIALOG.ENTER_NUMBER')}
                value={patient.animalControlLicense}
                onChange={(e) =>
                  updatePatient({ animalControlLicense: e.target.value })
                }
              />
            </div>
            <div
              className={classNames(styles.signatureLicenseRow, {
                [styles.signatureLicenseRowNoSignature]: !showSignature,
              })}
            >
              {showSignature && (
                <div className={styles.signatureBlock}>
                  <SignatureView
                    disabled={disabled}
                    signatureOptions={signatureOptions}
                    signatureString={signatureString}
                    signerName={signerName}
                    onEdit={onEditSignature}
                    onSelect={onSelectSignature}
                  />
                </div>
              )}
              <div className={styles.licenseField}>
                <KibField
                  required
                  disabled={disabled}
                  label={t('Common:LICENSE')}
                  value={patient.license}
                  onChange={(e) => updatePatient({ license: e.target.value })}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {showSignature && !showPatientDetails && (
        <div className={styles.signatureBlock}>
          <SignatureView
            disabled={disabled}
            signatureOptions={signatureOptions}
            signatureString={signatureString}
            signerName={signerName}
            onEdit={onEditSignature}
            onSelect={onSelectSignature}
          />
        </div>
      )}
    </div>
  )
}

export default ChirpChargesVaccineDetails
